import { useCallback, useEffect, useState } from 'react';

/**
 * The alert preferences of one person on one computer.
 *
 * Per browser and not per account, deliberately: a secretary at the front desk with speakers
 * on and one working from home at night want different things, and the same person wants
 * different things in each place. Nothing here reaches the server, so nobody can turn off
 * another person's chime — which is exactly why this is separate from the office-wide
 * settings screen (the email switch and address, which SHOULD be shared).
 *
 * Every read and write is wrapped: a private window, cleared site data or a browser set to
 * block storage throws on access rather than returning null. When it throws, the defaults
 * apply and the choice simply does not survive a reload.
 */

const STORAGE_KEY = 'inquiries.alerts.settings';

/*
 * ⚠️ There was a `inquiries.alerts.muted` key here, honoured on first read so nobody who had
 * muted would get a surprise chime. It is gone deliberately, and is NOT to be reinstated: the
 * button that wrote it existed for less than a day before being replaced by this panel, and
 * anyone who pressed it once - to see what it did - was left permanently silent with no
 * setting on screen explaining why. It cost an afternoon of "there is no sound" that was not a
 * bug. A stale key that silences a person invisibly is worse than an unexpected chime.
 */

export interface QueueAlertSettings {
  /** The chime. Off leaves the badge and the tab title working. */
  sound: boolean;
  /** 0–100. Meaningless while sound is off, but kept so unmuting restores the old level. */
  volume: number;
  /** The desktop notification that appears over other windows. */
  desktopNotification: boolean;
  /** The count in the tab title. Silent, and the only signal that needs no permission. */
  tabTitle: boolean;
}

export const DEFAULT_ALERT_SETTINGS: QueueAlertSettings = {
  sound: true,
  volume: 60,
  desktopNotification: true,
  tabTitle: true,
};

function clampVolume(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ALERT_SETTINGS.volume;
  return Math.min(100, Math.max(0, Math.round(n)));
}

export function readAlertSettings(): QueueAlertSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw === null) return DEFAULT_ALERT_SETTINGS;

    const parsed = JSON.parse(raw) as Partial<QueueAlertSettings> | null;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_ALERT_SETTINGS;

    /*
     * Field by field rather than a spread of whatever was stored: this value can be edited by
     * hand in devtools, and a stored `sound: "yes"` would otherwise be truthy forever.
     */
    return {
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : DEFAULT_ALERT_SETTINGS.sound,
      volume: clampVolume(parsed.volume),
      desktopNotification: typeof parsed.desktopNotification === 'boolean'
        ? parsed.desktopNotification
        : DEFAULT_ALERT_SETTINGS.desktopNotification,
      tabTitle: typeof parsed.tabTitle === 'boolean' ? parsed.tabTitle : DEFAULT_ALERT_SETTINGS.tabTitle,
    };
  } catch {
    return DEFAULT_ALERT_SETTINGS;
  }
}

function writeAlertSettings(settings: QueueAlertSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Nothing to do — the preference will not survive a reload.
  }
}

/*
 * Both the header control and the alert itself read these settings, and they are separate
 * React trees as far as state is concerned. This event keeps them in step: without it,
 * turning the chime off in the header left the running alert with the old value until a
 * reload. `storage` is not enough - it fires in OTHER tabs, never the one that wrote.
 */
const CHANGE_EVENT = 'inquiries-alert-settings-changed';

export function useQueueAlertSettings() {
  const [settings, setSettings] = useState<QueueAlertSettings>(readAlertSettings);

  useEffect(() => {
    const reread = () => setSettings(readAlertSettings());

    window.addEventListener(CHANGE_EVENT, reread);
    // A change made in another tab of the same browser, which is the same person.
    window.addEventListener('storage', reread);

    return () => {
      window.removeEventListener(CHANGE_EVENT, reread);
      window.removeEventListener('storage', reread);
    };
  }, []);

  const update = useCallback((patch: Partial<QueueAlertSettings>) => {
    const next = { ...readAlertSettings(), ...patch };
    if (patch.volume !== undefined) next.volume = clampVolume(patch.volume);

    writeAlertSettings(next);
    setSettings(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { settings, update };
}
